import {
    Box,
    VStack,
    HStack,
    Text,
    Icon,
    Flex,
    useToast,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogCloseButton,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    InputGroup,
    InputLeftElement,
    Input,
    Checkbox,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverHeader,
    PopoverBody,
    Portal,
    Tag,
    TagLabel,
    Divider,
} from '@chakra-ui/react';
import { useState, useMemo, useEffect, useRef } from 'react';
import SortArrowIcon, { SortIconDirection } from '../SortArrowIcon';
import { TbSearch, TbTrash, TbDiscount, TbCheck, TbCertificate } from 'react-icons/tb';
import { OEMSettings, OrderingMarketplace } from '../../hooks/useGetSettings';
import { useGetLocations } from '../../hooks/useGetLocations';
import { useNavigate } from 'react-router-dom';
import { useSettingsDraft } from '../../pages/settings/SettingsDraftContext';
import { useUpdateSettings } from '../../hooks/useUpdateSettings';
import { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import PartBayButton from '../PartBayButton';
import Pagination from '../Pagination';
import BulkActionBar, { BulkActionBarItem } from '../BulkActionBar';
import { InlineLocationListCell } from './InsuranceInlineCells';
import OemBulkCertificationsModal from './OemBulkCertificationsModal';
import { toLocationList, type LocationList } from './locationListValue';
import {
    SettingsEmptyState,
    SettingsPageActions,
    SettingsRowActions,
    SettingsTable,
    SettingsTableFooter,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
} from './primitives';
import {
    PARTBAY_BLUE,
    SETTINGS_TABLE_GUTTER,
} from './primitives/constants';

interface OEMInterface {
    oems: Array<OEMSettings>;
    oemOptions: Array<{ _id: string; name: string; similarMakes: Array<{ _id: string; name: string }>; }>;
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
}

/** Setting certifications and removing makes are the only bulk changes. */
const BULK_ACTIONS: BulkActionBarItem[] = [
    { id: 'certifications', label: 'Certifications', icon: TbCertificate as React.ElementType },
    { id: 'remove', label: 'Remove', icon: TbTrash as React.ElementType, danger: true },
];

const MARKETPLACE_LABELS: Record<OrderingMarketplace, string> = {
    ccc: 'CCC',
    opstrax: 'OPSTrax',
    collisionlink: 'CollisionLink',
    partstrader: 'PartsTrader',
};

const formatTierPercent = (value: number) => {
    const pct = value * 100;
    const rounded = Math.round(pct * 100) / 100;
    return `${rounded}%`;
};

const formatTierRange = (min: number, max: number) => {
    if (max >= 1) return `${formatTierPercent(min)}+`;
    return `${formatTierPercent(min)} - ${formatTierPercent(max)}`;
};

const getMaxRebate = (rebate: NonNullable<OEMSettings['rebate']>): number => {
    if (!rebate.thressholds.length) return 0;
    return rebate.thressholds.reduce((max, t) => Math.max(max, t.rebate), 0);
};

/**
 * The rebate summary, opening the OEM editor on click. Marketplaces and tiers
 * are a structure rather than a single value, so the cell links into the editor
 * that owns them instead of editing in place, and the hover card gives the
 * detail without the trip.
 */
const renderRebateCell = (oem: OEMSettings, onEdit: () => void) => {
    if (!oem.rebate) {
        return (
            <Text
                as="button"
                fontSize="sm"
                textAlign="left"
                color="gray.400"
                _hover={{ color: PARTBAY_BLUE, textDecoration: 'underline' }}
                onClick={onEdit}
            >
                None
            </Text>
        );
    }
    const { rebate } = oem;
    const maxRebate = getMaxRebate(rebate);
    const triggerLabel = rebate.thressholds.length > 0
        ? `Up to ${formatTierPercent(maxRebate)}`
        : 'Enrolled';

    return (
        <Popover trigger="hover" placement="left-start" gutter={12} openDelay={120} closeDelay={120}>
            <PopoverTrigger>
                <Text
                    as="button"
                    fontSize="sm"
                    textAlign="left"
                    color="gray.700"
                    _hover={{ color: PARTBAY_BLUE, textDecoration: 'underline' }}
                    onClick={onEdit}
                >
                    {triggerLabel}
                </Text>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    minW="320px"
                    maxW="360px"
                    boxShadow="none"
                    borderColor="gray.200"
                    _focus={{ outline: 'none' }}
                >
                    <PopoverArrow />
                    <PopoverHeader
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                        bg="gray.50"
                        py={3}
                        px={4}
                    >
                        <HStack spacing={2}>
                            <Icon as={TbDiscount as React.ElementType} color="blue.500" boxSize={4} />
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                Rebate Program
                            </Text>
                        </HStack>
                    </PopoverHeader>
                    <PopoverBody p={4}>
                        <VStack align="stretch" spacing={3.5}>
                            <Box>
                                <Text
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="gray.500"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    mb={1.5}
                                >
                                    Required Marketplaces
                                </Text>
                                {rebate.requiredMarketplaces.length === 0 ? (
                                    <Text fontSize="xs" color="gray.400">None specified</Text>
                                ) : (
                                    <HStack wrap="wrap" spacing={1.5}>
                                        {rebate.requiredMarketplaces.map((m) => (
                                            <Tag
                                                key={m}
                                                size="sm"
                                                colorScheme="partbay-blue"
                                                borderRadius="full"
                                                variant="subtle"
                                            >
                                                <TagLabel fontSize="xs">{MARKETPLACE_LABELS[m] ?? m}</TagLabel>
                                            </Tag>
                                        ))}
                                    </HStack>
                                )}
                            </Box>

                            <Divider />

                            <Box>
                                <Text
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="gray.500"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    mb={1.5}
                                >
                                    Rebate Tiers
                                </Text>
                                {rebate.thressholds.length === 0 ? (
                                    <Text fontSize="xs" color="gray.400">No tiers configured</Text>
                                ) : (
                                    <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                                        <Table size="sm" variant="simple">
                                            <Thead bg="gray.50">
                                                <Tr>
                                                    <Th
                                                        fontSize="2xs"
                                                        color="gray.600"
                                                        fontWeight="semibold"
                                                        textTransform="uppercase"
                                                        py={1.5}
                                                        px={3}
                                                    >
                                                        Threshold
                                                    </Th>
                                                    <Th
                                                        fontSize="2xs"
                                                        color="gray.600"
                                                        fontWeight="semibold"
                                                        textTransform="uppercase"
                                                        py={1.5}
                                                        px={3}
                                                        isNumeric
                                                    >
                                                        Rebate
                                                    </Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {rebate.thressholds.map((t, idx) => (
                                                    <Tr key={idx}>
                                                        <Td fontSize="xs" color="gray.700" py={1.5} px={3}>
                                                            {formatTierRange(t.minThresshold, t.maxThresshold)}
                                                        </Td>
                                                        <Td fontSize="xs" color="gray.700" py={1.5} px={3} isNumeric fontWeight="semibold">
                                                            {formatTierPercent(t.rebate)}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                )}
                            </Box>

                            {rebate.overrideDrpThresshold && (
                                <Box
                                    bg="blue.50"
                                    border="1px solid"
                                    borderColor="blue.100"
                                    borderRadius="md"
                                    px={3}
                                    py={2}
                                >
                                    <HStack spacing={2} align="flex-start">
                                        <Icon
                                            as={TbCheck as React.ElementType}
                                            color="blue.500"
                                            boxSize={3.5}
                                            mt="2px"
                                            flexShrink={0}
                                        />
                                        <Text fontSize="xs" color="blue.700" lineHeight="short">
                                            Overrides DRP ordering thresholds
                                        </Text>
                                    </HStack>
                                </Box>
                            )}
                        </VStack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

/**
 * OEM makes. Certification edits and row deletes are buffered in the settings
 * draft, so nothing is written until the floating Save/Discard bar commits it.
 * Adding a make and the full editor run through the OEM modal, which owns its
 * own save.
 */
const OEM = ({ oems, oemOptions, refetch }: OEMInterface) => {
    const navigate = useNavigate();
    const { getDomain, setDomain, isDirty } = useSettingsDraft();
    const updateSettings = useUpdateSettings();
    const { data: locationsData } = useGetLocations();
    // Certifications are held per location, so the picker needs the org's shops.
    const locations = locationsData?.locations ?? [];
    const localOems = getDomain('oem', oems) ?? oems;
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isBulkCertificationsOpen, setIsBulkCertificationsOpen] = useState(false);
    /** Makes awaiting delete confirmation: one row, or the whole selection. */
    const [removeTargets, setRemoveTargets] = useState<OEMSettings[] | null>(null);
    const removeCancelRef = useRef<HTMLButtonElement>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc' | null>(null);
    const { scrollRef: tableScrollRef, pageSize: itemsPerPage } = useDynamicPageSize();

    const toast = useToast();

    const filteredOems = useMemo(() => {
        let result = localOems;

        if (searchTerm) {
            result = result.filter(o =>
                o.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (nameSortDirection) {
            result = [...result].sort((a, b) => {
                const comparison = a.name.localeCompare(b.name);
                return nameSortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return result;
    }, [localOems, searchTerm, nameSortDirection]);

    const totalPages = useMemo(
        () => Math.ceil(filteredOems.length / itemsPerPage),
        [filteredOems.length, itemsPerPage]
    );

    // The page size follows the viewport, so a resize can shrink the page
    // count; clamp so the user is never stranded on a now-empty page.
    useEffect(() => {
        setCurrentPage((prev) => Math.min(prev, Math.max(1, totalPages)));
    }, [totalPages]);

    const currentItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredOems.slice(startIndex, endIndex);
    }, [filteredOems, currentPage, itemsPerPage]);

    // Search can hide a selected row; drop those ids so the bulk bar never acts
    // on makes that are no longer on screen.
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(filteredOems.map((oem) => oem.makeId));
            const next = new Set(Array.from(prev).filter((id) => visible.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [filteredOems]);

    const allPageSelected =
        currentItems.length > 0 && currentItems.every((oem) => selectedIds.has(oem.makeId));
    const somePageSelected = currentItems.some((oem) => selectedIds.has(oem.makeId));

    const selectedOems = useMemo(
        () => localOems.filter((oem) => selectedIds.has(oem.makeId)),
        [localOems, selectedIds],
    );

    const handleToggleSelect = (makeId: string, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(makeId);
            else next.delete(makeId);
            return next;
        });
    };

    // Header checkbox covers the current page only, matching what is visible.
    const handleSelectAllOnPage = (checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            currentItems.forEach((oem) => {
                if (checked) next.add(oem.makeId);
                else next.delete(oem.makeId);
            });
            return next;
        });
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleNameSortToggle = () => {
        setNameSortDirection(prev => {
            if (prev === null) return 'asc';
            if (prev === 'asc') return 'desc';
            return null;
        });
        setCurrentPage(1);
    };

    const getNameSortDirection = (): SortIconDirection => {
        if (nameSortDirection === 'asc') return 'asc';
        if (nameSortDirection === 'desc') return 'desc';
        return 'none';
    };

    // Add and edit are the same full-screen OEM flow; 'new' is the add case.
    const handleAddOem = () => navigate('/settings/oem/new');

    /**
     * `step` lands the flow on the screen the cell belongs to, so the rebate
     * cell opens on the rebates step rather than making the user click Next.
     */
    const handleEditOem = (oem: OEMSettings, step?: 'rebates') =>
        navigate(`/settings/oem/${oem.makeId}`, step ? { state: { step } } : undefined);

    /**
     * Deleting is committed by the confirmation dialog rather than staged: the
     * dialog is the confirmation, so there is nothing left to save. Only the
     * removal is written — other pending cell edits stay in the draft, with the
     * deleted rows dropped from it so a later save cannot resurrect them.
     */
    const handleConfirmRemove = () => {
        if (!removeTargets) return;
        const ids = new Set(removeTargets.map((oem) => oem.makeId));
        const removedCount = removeTargets.length;
        updateSettings.mutate(
            { oem: oems.filter((oem) => !ids.has(oem.makeId)) },
            {
                onSuccess: () => {
                    if (isDirty) {
                        setDomain('oem', localOems.filter((oem) => !ids.has(oem.makeId)));
                    }
                    setSelectedIds(new Set());
                    setRemoveTargets(null);
                    refetch();
                    toast({
                        title:
                            removedCount === 1
                                ? `${removeTargets[0].name} removed`
                                : `${removedCount} OEMs removed`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    });
                },
                onError: (error) => {
                    toast({
                        title: 'Error removing OEM',
                        description: error.message || 'Please try again.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    /**
     * Stage one make's change. POST /settings/update replaces the whole oem
     * array, so the draft always holds the complete list.
     */
    const handleCertificationsSave = (oem: OEMSettings, next: LocationList) => {
        setDomain(
            'oem',
            localOems.map((o) => (o.makeId === oem.makeId ? { ...o, certifications: next } : o)),
        );
    };

    const handleBulkCertificationsApply = (value: Array<string> | 'all') => {
        const next = toLocationList(value, locations);
        setDomain(
            'oem',
            localOems.map((oem) =>
                selectedIds.has(oem.makeId) ? { ...oem, certifications: next } : oem,
            ),
        );
        setIsBulkCertificationsOpen(false);
        setSelectedIds(new Set());
    };

    if (localOems.length === 0) {
        return (
            <SettingsEmptyState
                icon="car"
                title="No OEMs configured yet"
                description="Add your first OEM to set certifications or a rebate program"
                action={{ label: 'Add OEM', onClick: handleAddOem }}
            />
        );
    }

    return (
        <>
            <SettingsPageActions>
                <PartBayButton
                    size="sm"
                    variant="solid"
                    colorScheme="partbay-blue"
                    onClick={handleAddOem}
                    text="Add OEM"
                />
            </SettingsPageActions>

            <HStack
                justify="space-between"
                align="center"
                gap={4}
                px={SETTINGS_TABLE_GUTTER}
                pb={4}
                flexShrink={0}
            >
                <InputGroup size="sm" maxW="500px">
                    <InputLeftElement>
                        <Icon as={TbSearch as React.ElementType} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search by make name..."
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        bg="white"
                    />
                </InputGroup>
            </HStack>

            <SettingsTable
                scrollRef={tableScrollRef}
                head={
                    <>
                        <SettingsTh w="44px">
                            <Checkbox
                                size="sm"
                                colorScheme="partbay-blue"
                                isChecked={allPageSelected}
                                isIndeterminate={!allPageSelected && somePageSelected}
                                isDisabled={currentItems.length === 0}
                                onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                                aria-label="Select all makes on this page"
                            />
                        </SettingsTh>
                        <SettingsTh
                            color={nameSortDirection ? '#005BC4' : 'gray.500'}
                            cursor="pointer"
                            onClick={handleNameSortToggle}
                            _hover={{ color: '#005BC4' }}
                            userSelect="none"
                            transition="color 0.2s"
                        >
                            <Flex align="center" gap="6px">
                                NAME
                                <Box lineHeight={0} color={nameSortDirection ? '#005BC4' : 'gray.400'}>
                                    <SortArrowIcon direction={getNameSortDirection()} size={12} />
                                </Box>
                            </Flex>
                        </SettingsTh>
                        <SettingsTh minW="180px">CERTIFICATIONS</SettingsTh>
                        <SettingsTh minW="180px">REBATES</SettingsTh>
                        <SettingsTh textAlign="center">ACTIONS</SettingsTh>
                    </>
                }
            >
                {currentItems.map((oem) => (
                    // role="group" powers the chevron that fades in on hover
                    // for every editable cell in the row.
                    <Tr key={oem.makeId} role="group" _hover={{ bg: 'gray.25' }}>
                        <SettingsTd>
                            <Checkbox
                                size="sm"
                                colorScheme="partbay-blue"
                                isChecked={selectedIds.has(oem.makeId)}
                                onChange={(e) => handleToggleSelect(oem.makeId, e.target.checked)}
                                aria-label={`Select ${oem.name}`}
                            />
                        </SettingsTd>
                        <SettingsTd>
                            {/* The make name is the way into the full editor;
                                certifications edit in place. */}
                            <Text
                                as="button"
                                fontSize="sm"
                                textAlign="left"
                                color="gray.800"
                                _hover={{ color: PARTBAY_BLUE, textDecoration: 'underline' }}
                                onClick={() => handleEditOem(oem)}
                            >
                                {oem.name}
                            </Text>
                        </SettingsTd>
                        <SettingsTd>
                            <InlineLocationListCell
                                value={oem.certifications ?? 'all'}
                                locations={locations}
                                ariaLabel={`Edit ${oem.name} certifications`}
                                onSave={(next) => handleCertificationsSave(oem, next)}
                            />
                        </SettingsTd>
                        <SettingsTd>
                            {renderRebateCell(oem, () => handleEditOem(oem, 'rebates'))}
                        </SettingsTd>
                        <SettingsTd textAlign="center">
                            <SettingsRowActions
                                onEdit={() => handleEditOem(oem)}
                                onDelete={() => setRemoveTargets([oem])}
                                editLabel="Edit OEM"
                                deleteLabel="Delete OEM"
                            />
                        </SettingsTd>
                    </Tr>
                ))}
            </SettingsTable>

            <SettingsTableFooter>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    py={0}
                />
            </SettingsTableFooter>

            <BulkActionBar
                selectedCount={selectedOems.length}
                actions={BULK_ACTIONS}
                onClearSelection={() => setSelectedIds(new Set())}
                onAction={(actionId) => {
                    if (actionId === 'certifications') setIsBulkCertificationsOpen(true);
                    else setRemoveTargets(selectedOems);
                }}
                // Staged edits float their own save bar; stack above it.
                bottom={isDirty ? 20 : 4}
            />

            <OemBulkCertificationsModal
                isOpen={isBulkCertificationsOpen}
                selectedCount={selectedOems.length}
                locations={locations}
                onApply={handleBulkCertificationsApply}
                onClose={() => setIsBulkCertificationsOpen(false)}
            />

            <AlertDialog
                isOpen={removeTargets !== null}
                leastDestructiveRef={removeCancelRef}
                onClose={() => setRemoveTargets(null)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="md">
                            {removeTargets?.length === 1 ? 'Remove OEM' : 'Remove OEMs'}
                        </AlertDialogHeader>
                        <AlertDialogCloseButton />
                        <AlertDialogBody>
                            <Text fontSize="sm" color="gray.600">
                                This removes{' '}
                                <Text as="span" fontWeight="semibold" color="gray.800">
                                    {removeTargets?.length === 1
                                        ? removeTargets[0].name
                                        : `${removeTargets?.length ?? 0} makes`}
                                </Text>{' '}
                                and {removeTargets?.length === 1 ? 'its' : 'their'} certifications,
                                thresholds and rebate settings. You can add{' '}
                                {removeTargets?.length === 1 ? 'it' : 'them'} back at any time.
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter gap={2}>
                            <Button
                                ref={removeCancelRef}
                                size="sm"
                                variant="outlineGray"
                                onClick={() => setRemoveTargets(null)}
                                isDisabled={updateSettings.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="red"
                                isLoading={updateSettings.isPending}
                                loadingText="Removing"
                                onClick={handleConfirmRemove}
                            >
                                Remove
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

        </>
    );
};

export default OEM;
